import { useEffect, useMemo, type CSSProperties } from 'react';

// Brand-aligned, restrained palette — the green family with one cool companion.
const COLORS = ['var(--iris)', 'var(--iris-strong)', 'var(--viridian)', 'var(--success)'];
const PIECES = 36;
const DURATION_MS = 2200;

/**
 * A brief, restrained acknowledgement — a short fall of brand-coloured confetti —
 * shown for a moment when something worth marking happens (e.g. an optimization is
 * executed). Non-interactive; auto-dismisses via onDone. Respects reduced-motion.
 */
export function Confetti({ active, onDone }: { active: boolean; onDone: () => void }) {
  const pieces = useMemo(() => {
    if (!active) return [];
    // Decorative only — honour reduced-motion by skipping the animation entirely.
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    )
      return [];
    return Array.from({ length: PIECES }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      dx: (Math.random() * 2 - 1) * 140,
      rot: 360 + Math.random() * 720,
      delay: Math.random() * 400,
      duration: 1800 + Math.random() * 1400,
      color: COLORS[i % COLORS.length],
      w: 6 + Math.random() * 6,
      h: 9 + Math.random() * 8,
      round: Math.random() > 0.7,
    }));
  }, [active]);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(onDone, DURATION_MS);
    return () => clearTimeout(t);
  }, [active, onDone]);

  if (!active) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[100] overflow-hidden" aria-hidden>
      <style>{`
        @keyframes lz-confetti-fall {
          0% { transform: translate3d(0, -12vh, 0) rotate(0deg); opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translate3d(var(--dx), 112vh, 0) rotate(var(--rot)); opacity: 0; }
        }
      `}</style>

      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute top-0 block"
          style={
            {
              left: `${p.left}%`,
              width: p.w,
              height: p.h,
              backgroundColor: p.color,
              borderRadius: p.round ? '9999px' : '1px',
              '--dx': `${p.dx}px`,
              '--rot': `${p.rot}deg`,
              animation: `lz-confetti-fall ${p.duration}ms cubic-bezier(.2,.6,.4,1) ${p.delay}ms forwards`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
